import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE, OG_IMAGE } from "@/lib/es-pages";
import { ORG_REF, PHYSICIAN_REF } from "@/lib/medical-schema";
import poriesCartoon from "@/assets/pories-cartoon-dialysis-center-gastric-bypass.jpg.asset.json";

const URL = `${SITE}/es/obesidad-y-diabetes`;
const CARTOON_CAPTION =
  "“Abuelo, ¿por qué no te hiciste un bypass gástrico?” Caricatura cortesía del Dr. Walter Pories, usada con permiso.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Puede la cirugía bariátrica curar la diabetes tipo 2?",
    a: "No. Puede poner la diabetes en remisión, a veces por una década o más, y puede mejorar de forma notable la sensibilidad a la insulina. Pero la remisión puede terminar, y por eso seguimos vigilando su azúcar en la sangre de por vida.",
  },
  {
    q: "¿Qué tan rápido mejora el azúcar en la sangre después de la cirugía?",
    a: "A menudo en cuestión de días, mucho antes de que se haya perdido un peso significativo. Ese efecto temprano se debe a cambios en la liberación de hormonas intestinales. La pérdida de peso que sigue es lo que lo mantiene.",
  },
  {
    q: "¿Qué operación funciona mejor para la diabetes?",
    a: "El bypass gástrico tiene la evidencia más sólida, con la manga gástrica muy cerca. La banda gástrica ajustable es la más débil de las tres, porque su beneficio metabólico depende casi por completo de cuánto peso pierda.",
  },
  {
    q: "¿Los medicamentos para bajar de peso funcionan tan bien como la cirugía?",
    a: "Funcionan bien, y para muchos pacientes son el primer paso correcto. Sin embargo, en estudios aleatorizados que los compararon directamente con la terapia médica, la cirugía ha logrado tasas de remisión mayores y más duraderas. Las dos opciones no son excluyentes.",
  },
];

export const Route = createFileRoute("/es/obesidad-y-diabetes")({
  head: () => {
    const base = esHead("diabetes");
    return {
      ...base,
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    ...esWebPageSchema("diabetes"),
                    "@type": "MedicalWebPage",
                    about: [
                      {
                        "@type": "MedicalCondition",
                        name: "Diabetes mellitus tipo 2",
                        code: { "@type": "MedicalCode", code: "E11", codingSystem: "ICD-10" },
                      },
                      {
                        "@type": "MedicalCondition",
                        name: "Obesidad",
                        code: { "@type": "MedicalCode", code: "E66", codingSystem: "ICD-10" },
                      },
                    ],
                    audience: {
                      "@type": "MedicalAudience",
                      audienceType: "Pacientes con obesidad y diabetes tipo 2",
                    },
                    specialty: "Bariatric",
                    reviewedBy: PHYSICIAN_REF,
                    author: PHYSICIAN_REF,
                    publisher: ORG_REF,
                    image: [
                      OG_IMAGE,
                      {
                        "@type": "ImageObject",
                        url: `${SITE}${poriesCartoon.url}`,
                        caption: CARTOON_CAPTION,
                        creditText: "Caricatura cortesía del Dr. Walter Pories, usada con permiso.",
                        creator: { "@type": "Person", name: "Dr. Walter Pories" },
                        copyrightNotice: "© Dr. Walter Pories, usada con permiso.",
                        copyrightHolder: { "@type": "Person", name: "Dr. Walter Pories" },
                        license: URL,
                        acquireLicensePage: URL,
                      },
                    ],
                  },
                  {
                    "@type": "FAQPage",
                    "@id": `${URL}#faq`,
                    url: URL,
                    inLanguage: "es-US",
                    isPartOf: { "@id": `${URL}#webpage` },
                    publisher: ORG_REF,
                    mainEntity: FAQS.map((f, i) => ({
                      "@type": "Question",
                      "@id": `${URL}#faq-${i + 1}`,
                      name: f.q,
                      inLanguage: "es-US",
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: EsDiabetesPage,
});

const LINK = "font-medium text-brand-logo hover:underline";

function EsDiabetesPage() {
  return (
    <EsPageShell
      page="diabetes"
      title="Obesidad, resistencia a la insulina y cirugía bariátrica"
      intro="Un mismo mecanismo detrás del exceso de peso y la diabetes tipo 2"
    >
      <EsSection heading="Dos problemas, un mismo mecanismo">
        <p>
          La diabetes tipo 2 y el exceso de peso no son dos problemas separados que
          casualmente van juntos. Comparten un mecanismo. Una vez que se entiende ese
          mecanismo, muchas cosas tienen sentido: por qué bajar de peso mejora el azúcar
          en la sangre, por qué algunos tratamientos para la diabetes funcionan mucho más
          rápido que otros, y por qué la resistencia a la insulina y la cirugía bariátrica
          aparecen tan a menudo en la misma conversación.
        </p>
        <p>
          Esta página explica qué es la resistencia a la insulina, cómo se mide y qué nos
          enseña la evidencia sobre cómo revertirla.
        </p>
      </EsSection>

      <EsSection id="resistencia-a-la-insulina" heading="Cómo el exceso de peso causa resistencia a la insulina">
        <p>
          La insulina le indica al cuerpo que es hora de sacar el azúcar de la sangre y
          llevarla a las células. Llamamos <em>resistencia a la insulina</em> a lo que
          ocurre cuando las células dejan de responder a la señal de la insulina. El
          páncreas intenta compensarlo enviando una señal más fuerte. Libera cada vez más
          insulina. Logra mantenerse al día, por un tiempo. La secreción de insulina sube
          y mantiene la glucosa en la sangre cerca de lo normal. Con el tiempo llega el día
          en que el páncreas ya no puede seguir el ritmo. Es entonces cuando aparece la
          diabetes tipo 2.
        </p>
        <p>
          El tejido adiposo (la grasa) puede ser un potente impulsor de la resistencia a
          la insulina, pero depende de dónde esté. La grasa almacenada en las
          extremidades (grasa periférica) es relativamente inofensiva. En cambio, la grasa
          asociada a los órganos, la grasa visceral, es muy potente desde el punto de
          vista metabólico. Libera ácidos grasos libres y otras señales inflamatorias que
          interfieren con la respuesta de las células a la señal de la insulina. La grasa
          que se acumula en el hígado altera especialmente el metabolismo de la glucosa.
        </p>
        <p>
          La actividad metabólica variable de los distintos tipos de grasa explica por qué
          personas con el mismo índice de masa corporal (IMC) pueden tener una salud
          metabólica muy diferente. La ubicación importa más que la cantidad.
        </p>
      </EsSection>

      <EsSection id="como-se-mide" heading="Cómo se mide la resistencia a la insulina">
        <p>
          Usamos varias pruebas para entender qué tan bien controla su cuerpo el azúcar en
          la sangre. Puede que las reconozca de su expediente.
        </p>
        <p>
          La glucosa en ayunas y la hemoglobina A1C (HbA1c) dan una idea bastante buena de
          la capacidad actual de su cuerpo para regular la glucosa. Ambas se han usado para
          diagnosticar la diabetes tipo 2.
        </p>
        <p>
          La glucosa en ayunas tiene la ventaja de la comodidad. Obtiene una respuesta en
          cinco segundos si tiene un glucómetro. También es barata. Puede conseguir un
          glucómetro decente por $30 en Walmart.
        </p>
        <p>
          La prueba de HbA1c requiere enviar un tubo de sangre al laboratorio, pero nos
          permite conocer su nivel promedio de glucosa durante unos tres meses, un dato
          inmensamente útil. La HbA1c reemplazó recientemente a la prueba de tolerancia a
          la glucosa como el estándar de oro para diagnosticar la diabetes tipo 2, y
          también se ha convertido en la medida estándar del control del azúcar.
        </p>
        <p>
          El modelo de evaluación de la homeostasis, que suele escribirse HOMA-IR, estima
          la resistencia en sí. Combina la glucosa y la insulina en ayunas en un solo
          número. Los investigadores lo usan para comparar grupos en distintos momentos
          antes y después del tratamiento, porque capta un cambio en la sensibilidad a la
          insulina que la HbA1c sola puede pasar por alto.
        </p>
        <p>
          La prueba oral de tolerancia a la glucosa mide qué tan rápido elimina una carga
          de azúcar. Dice algo tanto de la resistencia a la insulina como de la secreción
          de insulina.
        </p>
      </EsSection>

      <EsSection id="perdida-de-peso" heading="Lo que logra la pérdida de peso">
        <p>
          Bajar de peso mejora de forma confiable la sensibilidad a la insulina. Reduzca la
          grasa visceral y las células vuelven a responder a la insulina. El azúcar en la
          sangre se comporta.
        </p>
        <p>
          La cantidad de peso perdido influye en la mejoría que se observa. Una pérdida
          modesta ayuda. Una pérdida significativa ayuda considerablemente más. Los
          programas de dieta suficientemente intensivos (piense en seis a ocho semanas en
          un spa para bajar de peso o un centro de tratamiento con internación) pueden
          poner la diabetes tipo 2 en remisión sin cirugía ni medicamentos.
        </p>
        <p>
          El problema de los centros de dieta fuera de casa es que, tarde o temprano,
          todos regresan a casa. Al volver a la rutina normal, lo habitual es recuperar
          todo el peso. La <em>pérdida de peso sostenida</em> es lo que determina si la
          remisión de la diabetes perdura.
        </p>
      </EsSection>

      <EsSection id="medicamentos" heading="Lo que pueden hacer los medicamentos para bajar de peso">
        <p>
          Los medicamentos más nuevos, entre ellos la{" "}
          <Link to="/es/semaglutida" className={LINK}>semaglutida</Link> y la{" "}
          <Link to="/es/tirzepatida" className={LINK}>tirzepatida</Link>, se desarrollaron
          para tratar la diabetes tipo 2 antes de usarse solo para el peso. Actúan en dos
          frentes. Le ayudan a comer menos y actúan directamente sobre el metabolismo de
          la glucosa.
        </p>
        <p>
          En los estudios, los pacientes perdieron un peso considerable, tuvieron grandes
          mejorías en la glucosa en la sangre y muchos pudieron reducir o suspender otros
          medicamentos para la diabetes. Son tratamientos continuos. Funcionan mientras
          los toma, y el azúcar tiende a subir de nuevo si se suspenden.
        </p>
      </EsSection>

      <EsSection id="estudios" heading="Resistencia a la insulina y cirugía bariátrica: lo que muestran los estudios">
        <p>
          La cirugía se ha comparado directamente con la terapia médica en estudios
          controlados aleatorizados, y eso hace que esta evidencia sea inusualmente sólida
          para un campo quirúrgico.
        </p>
        <h3 className="text-xl md:text-2xl font-serif text-brand-mid pt-2">El estudio STAMPEDE</h3>
        <p>
          El estudio STAMPEDE asignó al azar a 150 pacientes con diabetes tipo 2 y un IMC
          de 27 a 43 a uno de tres grupos: terapia médica intensiva sola, o esa misma
          terapia más{" "}
          <Link to="/es/bypass-gastrico" className={LINK}>cirugía de bypass gástrico</Link>{" "}
          o <Link to="/es/manga-gastrica" className={LINK}>manga gástrica</Link>. A los
          cinco años, el 29% del grupo de bypass y el 23% del grupo de manga habían
          alcanzado una HbA1c de 6.0% o menos, frente al 5% de quienes recibieron solo
          terapia médica. La pérdida de peso fue del 23% con bypass, 19% con manga y 5% con
          terapia médica. El uso de insulina, los triglicéridos, el colesterol HDL y la
          calidad de vida favorecieron a la cirugía.
        </p>
        <h3 className="text-xl md:text-2xl font-serif text-brand-mid pt-2">
          El estudio de Roma (sin un acrónimo ingenioso)
        </h3>
        <p>
          Otro estudio controlado aleatorizado en Roma siguió a los pacientes durante diez
          años, los datos más largos disponibles. La remisión de la diabetes a los diez
          años fue del 5.5% con terapia médica, 25% después del bypass gástrico y 50%
          después de la derivación biliopancreática.
        </p>
        <p>
          Dos hallazgos merecen énfasis. La mayoría de los pacientes que recayeron seguían
          teniendo un buen control de la glucosa a los diez años. Y los grupos quirúrgicos
          tuvieron muchas menos complicaciones relacionadas con la diabetes que el grupo
          tratado con medicamentos.
        </p>
        <p>
          Los resultados varían según el procedimiento. En personas con obesidad y diabetes
          tipo 2, el bypass gástrico y la manga gástrica superan a la banda gástrica
          ajustable, que produce menos pérdida de peso y un efecto metabólico
          correspondientemente más débil.
        </p>
        <figure className="mx-auto max-w-xs">
          <img
            src={poriesCartoon.url}
            alt="Caricatura del Dr. Walter Pories: un abuelo con complicaciones de la diabetes afuera de un centro de diálisis, mientras su nieto le pregunta por qué no se hizo un bypass gástrico."
            width={409}
            height={392}
            loading="lazy"
            decoding="async"
            className="w-full rounded-lg border border-border"
          />
          <figcaption className="mt-2 text-sm text-muted-foreground">{CARTOON_CAPTION}</figcaption>
        </figure>
      </EsSection>

      <EsSection id="por-que-mas-rapido" heading="Por qué la cirugía actúa más rápido que la pérdida de peso sola">
        <p>
          En los primeros días después de un bypass gástrico ocurre algo que mejora el
          azúcar en la sangre antes de que se haya perdido un peso significativo. Los
          pacientes a menudo se van a casa con dosis de insulina más bajas que con las que
          llegaron.
        </p>
        <p>
          Redirigir el paso de los alimentos cambia cómo y cuándo el intestino libera
          hormonas. El GLP-1 y el PYY suben y la grelina baja. Esas hormonas mejoran la
          secreción de insulina después de las comidas y reducen el apetito. La pérdida de
          peso que sigue hace luego el trabajo más lento de restaurar la sensibilidad a la
          insulina. Los dos efectos se suman, y por eso este campo llama cada vez más a
          estas operaciones cirugía metabólica y no simplemente cirugía para bajar de peso.
        </p>
      </EsSection>

      <EsSection id="remision" heading="La remisión no siempre es permanente">
        <p>
          Remisión no es lo mismo que curación. En los datos a diez años mencionados, la
          mayoría de los pacientes que alcanzaron la remisión vieron con el tiempo subir de
          nuevo el azúcar, aunque siguieron mejor controlados que antes del tratamiento.
        </p>
        <p>
          Dos factores predicen resultados duraderos. El primero es cuánto tiempo tuvo
          diabetes antes del tratamiento: a los pacientes tratados antes, cuando la función
          del páncreas no ha disminuido mucho, les va mejor. El segundo es si el peso se
          mantiene. Por eso consideramos la operación como el inicio de una atención a
          largo plazo y no como el final del tratamiento.
        </p>
      </EsSection>

      <EsSection id="candidatos" heading="Quién es candidato">
        <p>
          Las guías actuales apoyan la cirugía con un IMC de 35 o más solo por el peso, y
          de 30 a 34.9 cuando hay diabetes tipo 2. Para pacientes de ascendencia del este
          de Asia, esos umbrales bajan a 32.5 y 27.5.
        </p>
        <p>
          Nuestra página de{" "}
          <Link to="/es/requisitos-para-cirugia" className={LINK}>
            requisitos para la cirugía bariátrica
          </Link>{" "}
          explica todos los criterios, y nuestra{" "}
          <Link to="/es/cobertura-de-seguro" className={LINK}>guía de cobertura de seguro</Link>{" "}
          explica qué aprueban los principales planes. Si ya tuvo una operación y la
          diabetes regresó, vea nuestras opciones de{" "}
          <Link to="/es/cirugia-de-revision" className={LINK}>cirugía de revisión</Link>.
        </p>
      </EsSection>

      <EsSection id="faq" heading="Obesidad y diabetes: preguntas frecuentes">
        {FAQS.map((f, i) => (
          <div key={f.q} id={`faq-${i + 1}`}>
            <h3 className="text-lg font-semibold text-brand-mid">{f.q}</h3>
            <p>{f.a}</p>
          </div>
        ))}
      </EsSection>

      <EsSection id="hablemos" heading="Hablemos.">
        <p>
          Si vive con diabetes tipo 2 y tiene exceso de peso, tiene más opciones que hace
          una década, y mejor evidencia que las respalda. Solicite una consulta y
          revisaremos los medicamentos, la cirugía y cómo podría ser una combinación de
          ambos en su caso.
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Link
            to="/es/contacto"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Solicite una Consulta
          </Link>
          <Link
            to="/es/requisitos-para-cirugia"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Requisitos para la Cirugía Bariátrica
          </Link>
        </div>
      </EsSection>

      <EsSection id="referencias" heading="Referencias">
        <ol className="list-decimal pl-6 space-y-2 text-sm text-muted-foreground leading-snug">
          <li>
            Schauer PR, Bhatt DL, Kirwan JP, et al. Bariatric Surgery versus Intensive
            Medical Therapy for Diabetes &mdash; 5-Year Outcomes (STAMPEDE).{" "}
            <em>N Engl J Med.</em> 2017;376(7):641-651. doi:10.1056/NEJMoa1600869
          </li>
          <li>
            Mingrone G, Panunzi S, De Gaetano A, et al. Metabolic surgery versus
            conventional medical therapy in patients with type 2 diabetes: 10-year
            follow-up of an open-label, single-centre, randomised controlled trial.{" "}
            <em>Lancet.</em> 2021;397(10271):293-304. doi:10.1016/S0140-6736(20)32649-0
          </li>
        </ol>
      </EsSection>
    </EsPageShell>
  );
}
